import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import { LifeBuoy, Ticket, CircleDot, CheckCircle2, PlusCircle, ListChecks, User2, Calendar, Search } from "lucide-react";

// Matches the base URL used elsewhere in ResidenceAdminDashboard.
const API_BASE = "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api";

const CATEGORY_OPTIONS = [
  { value: "SystemDown", label: "System is down" },
  { value: "BugNotWorking", label: "Something isn't working" },
  { value: "FeatureRequest", label: "Request extra development" },
  { value: "Other", label: "Other" },
];

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

const STATUS_STYLES = {
  New: "bg-slate-100 text-slate-700 border border-slate-300",
  Assigned: "bg-blue-100 text-blue-700 border border-blue-300",
  InProgress: "bg-amber-100 text-amber-700 border border-amber-300",
  Resolved: "bg-emerald-100 text-emerald-700 border border-emerald-300",
  Closed: "bg-gray-200 text-gray-600 border border-gray-300",
};

const PRIORITY_DOT = {
  Low: "bg-slate-400",
  Medium: "bg-blue-500",
  High: "bg-amber-500",
  Critical: "bg-red-500",
};

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.New;
  const label = status === "InProgress" ? "In Progress" : status;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${style}`}>
      {label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const dot = PRIORITY_DOT[priority] || "bg-slate-400";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600">
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      {priority}
    </span>
  );
}

export default function MSASupportPage({ residenceManagerId: propId, residenceId, residenceName }) {
  // Falls back to whatever the login flow stored, if not passed as a prop.
  const residenceManagerId =
    propId ??
    (() => {
      try {
        const stored = JSON.parse(sessionStorage.getItem("residenceManager") || "null");
        return stored?.residenceManagerId ?? stored?.ResidenceManagerId ?? null;
      } catch {
        return null;
      }
    })();

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const TICKETS_PER_PAGE = 5;

  const [form, setForm] = useState({
    subject: "",
    description: "",
    category: "BugNotWorking",
    priority: "Medium",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (!residenceManagerId) return;
    setLoadingTickets(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_BASE}/tickets/manager/${residenceManagerId}`);
      if (!res.ok) throw new Error(`Failed to load tickets (${res.status})`);
      const data = await res.json();
      setTickets(data);
    } catch (err) {
      setLoadError(err.message || "Couldn't load your tickets.");
      toast.error("Failed to load your tickets");
    } finally {
      setLoadingTickets(false);
    }
  }, [residenceManagerId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!residenceManagerId) {
      toast.error("You need to be logged in to log a ticket.");
      return;
    }
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Please fill in both a subject and a description.");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Logging ticket...");

    try {
      const res = await fetch(`${API_BASE}/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ResidenceManagerId: residenceManagerId,
          Subject: form.subject,
          Description: form.description,
          Category: form.category,
          Priority: form.priority,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to log ticket (${res.status})`);
      }

      const created = await res.json();
      const ticketNumber = created.ticketNumber || created.TicketNumber;
      toast.success(`Ticket ${ticketNumber} logged - MSA Support has been notified`, { id: toastId });
      setForm({ subject: "", description: "", category: "BugNotWorking", priority: "Medium" });
      fetchTickets();
    } catch (err) {
      toast.error(err.message || "Something went wrong logging your ticket.", { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  const openCount = tickets.filter((t) => t.status !== "Resolved" && t.status !== "Closed").length;
  const resolvedCount = tickets.filter((t) => t.status === "Resolved" || t.status === "Closed").length;

  const filteredTickets = tickets.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.ticketNumber.toLowerCase().includes(q) ||
      t.subject.toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q) ||
      (t.category || "").toLowerCase().includes(q) ||
      (t.priority || "").toLowerCase().includes(q) ||
      (t.status || "").toLowerCase().includes(q) ||
      (t.assignedToTeamMemberName || "").toLowerCase().includes(q) ||
      (t.assignedToTeamMemberDesignation || "").toLowerCase().includes(q) ||
      (t.resolutionNotes || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / TICKETS_PER_PAGE));
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * TICKETS_PER_PAGE,
    currentPage * TICKETS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [tickets.length, search]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-sm shadow-blue-200">
              <LifeBuoy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">MSA Support</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Log an issue or request, and track progress on your tickets below.
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Residence</p>
            <p className="text-sm font-semibold text-gray-700">{residenceName || "—"}</p>
          </div>
        </div>
      </div>

      {/* Stat cards - matches the icon-badge stat card pattern used on Residence Stats / Maintenance tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-5 shadow-lg shadow-blue-200 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100 font-medium">Total Tickets</p>
              <p className="text-3xl font-bold mt-1">{tickets.length}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl">
              <Ticket className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-5 shadow-lg shadow-amber-200 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-50 font-medium">Open</p>
              <p className="text-3xl font-bold mt-1">{openCount}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl">
              <CircleDot className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 shadow-lg shadow-emerald-200 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-100 font-medium">Resolved / Closed</p>
              <p className="text-3xl font-bold mt-1">{resolvedCount}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Log a ticket card */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <PlusCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Log a new ticket</h2>
              <p className="text-xs text-gray-500">Tell us what's going on and we'll take it from here</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={handleChange("category")}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="text-gray-900">
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={form.priority}
                  onChange={handleChange("priority")}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p} className="text-gray-900">
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={form.subject}
                onChange={handleChange("subject")}
                placeholder="Short summary, e.g. 'Cannot log in to portal'"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={handleChange("description")}
                rows={4}
                placeholder="What's happening? Include any error messages or steps to reproduce."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-5 py-2.5 rounded-lg text-white font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {submitting ? "Logging ticket..." : "Log ticket"}
            </button>
          </form>
        </div>
      </div>

      {/* Ticket list */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <ListChecks className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Your tickets</h2>
              <span className="text-xs text-gray-600 bg-white px-2.5 py-1 rounded-full border border-gray-200 shadow-sm">
                {tickets.length} Total
              </span>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search your tickets..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="p-6">

          {loadingTickets && <p className="text-sm text-gray-500">Loading your tickets...</p>}
          {loadError && <p className="text-sm text-red-600">{loadError}</p>}

          {!loadingTickets && !loadError && tickets.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <LifeBuoy className="w-8 h-8 text-blue-400" />
              </div>
              <p className="text-gray-600 font-medium">No tickets logged yet</p>
              <p className="text-sm text-gray-400 mt-1">Tickets you log will appear here so you can track their progress</p>
            </div>
          )}

          {!loadingTickets && !loadError && tickets.length > 0 && filteredTickets.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Search className="w-8 h-8 text-blue-400" />
              </div>
              <p className="text-gray-600 font-medium">No tickets match your search</p>
              <p className="text-sm text-gray-400 mt-1">Try a different term</p>
            </div>
          )}

          {!loadingTickets && filteredTickets.length > 0 && (
            <div className="space-y-3">
              {paginatedTickets.map((t) => {
                const priorityBorder = {
                  Low: "border-l-slate-300",
                  Medium: "border-l-blue-400",
                  High: "border-l-amber-400",
                  Critical: "border-l-red-500",
                }[t.priority] || "border-l-gray-300";

                return (
                  <div
                    key={t.ticketId}
                    className={`rounded-lg border border-gray-200 border-l-4 ${priorityBorder} p-4 hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className="text-xs font-mono text-gray-400">{t.ticketNumber}</span>
                          <StatusPill status={t.status} />
                          <PriorityBadge priority={t.priority} />
                        </div>
                        <p className="font-semibold text-gray-900 truncate">{t.subject}</p>
                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>
                        {t.assignedToTeamMemberName && (
                          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                            <User2 className="w-3.5 h-3.5 text-gray-400" />
                            Assigned to {t.assignedToTeamMemberName} ({t.assignedToTeamMemberDesignation})
                          </p>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(t.createdDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!loadingTickets && filteredTickets.length > TICKETS_PER_PAGE && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-gray-500">
                Showing {(currentPage - 1) * TICKETS_PER_PAGE + 1}-
                {Math.min(currentPage * TICKETS_PER_PAGE, filteredTickets.length)} of {filteredTickets.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
